import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ArrowRight, ShoppingBag } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import SectionReveal from "@/components/SectionReveal";
import { api } from "@/auth/AuthContext";
import { useCart, fmt, fromPrice } from "@/journeys/CartContext";
import { useLang } from "@/contexts/LangContext";
import { jx, localizeJourney } from "@/i18n/journeyLabels";

export const CurrencyToggle = () => {
  const { currency, setCurrency } = useCart();
  return (
    <div className="inline-flex border border-[#EAE6DF] bg-white" data-testid="currency-toggle">
      {["AED", "USD"].map((c) => (
        <button key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}
          className={`px-4 py-2 text-xs font-bold tracking-[0.15em] transition-colors ${currency === c ? "bg-[#0E2A5C] text-white" : "text-[#4A4A4A] hover:text-[#0E2A5C]"}`}>
          {c}
        </button>
      ))}
    </div>
  );
};

export const CartLink = () => {
  const { items } = useCart();
  return (
    <Link to="/journeys/cart" data-testid="cart-link"
      className="relative inline-flex items-center gap-2 border border-[#EAE6DF] bg-white px-4 py-2 text-xs font-bold tracking-[0.15em] text-[#0E2A5C] hover:border-[#0E2A5C] transition-colors">
      <ShoppingBag size={14} /> CART
      {items.length > 0 && <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#C62828] text-white text-[10px] flex items-center justify-center" data-testid="cart-count">{items.length}</span>}
    </Link>
  );
};

export const JourneyCard = ({ j }) => {
  const { currency } = useCart();
  const { lang } = useLang();
  const L = jx(lang);
  const jl = localizeJourney(j, lang);
  const p = fromPrice(j, currency);
  return (
    <Link to={`/journeys/${j.slug}`} data-testid={`journey-card-${j.slug}`}
      className="group bg-white border border-[#EAE6DF] hover:shadow-xl hover:border-[#0E2A5C]/30 transition-all overflow-hidden flex flex-col">
      <div className="relative h-64 overflow-hidden">
        <img src={j.hero_image} alt={j.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-4 left-4 bg-[#0E2A5C]/90 text-[#D4AF37] text-[10px] font-bold tracking-[0.2em] px-3 py-1.5">{j.badge || j.category}</span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-heading text-xl text-[#0E2A5C] leading-snug">{jl.title}</h3>
        <p className="mt-1 text-xs uppercase tracking-[0.15em] text-[#4A4A4A] flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><MapPin size={11} />{j.destination}</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} />{j.duration_label}</span>
        </p>
        <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed line-clamp-2">{jl.description}</p>
        <ul className="mt-3 space-y-1">
          {(jl.highlights || []).slice(0, 3).map((h) => (
            <li key={h} className="text-xs text-[#0E2A5C] flex items-start gap-2"><span className="text-[#D4AF37] mt-0.5">✦</span>{h}</li>
          ))}
        </ul>
        <div className="mt-auto pt-5 flex items-end justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A]">{L.startingFrom}</p>
            <p className="font-heading text-xl text-[#C62828]">{j.price_on_request || !p ? L.priceOnRequest : `${fmt(p, currency)}`}</p>
            {!j.price_on_request && p && <p className="text-[10px] text-[#4A4A4A]">{jl.price_label || L.perPersonDouble}</p>}
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] text-[#0E2A5C] group-hover:text-[#C62828] transition-colors whitespace-nowrap">
            {L.viewJourney} <ArrowRight size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default function JourneysShop() {
  const [journeys, setJourneys] = useState(null);
  const { lang } = useLang();
  const L = jx(lang);
  useEffect(() => { api.get("/journeys").then((r) => setJourneys(r.data.journeys)).catch(() => setJourneys([])); }, []);
  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]">
      <SEO title="China Journeys — Curated Travel Packages" description="Curated journeys through China: Canton Fair, Signature China, Zhangjiajie Avatar Mountains, Xinjiang, Yunnan and more." path="/journeys" />
      <Header />
      <section className="pt-32 md:pt-40 pb-16 bg-[#0E2A5C] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_80%_20%,#D4AF37_0%,transparent_55%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">✦ {L.homeEyebrow}</p>
          <h1 className="font-heading text-4xl md:text-6xl mt-4 leading-tight">{L.collectionTitle}</h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl leading-relaxed">{L.collectionIntro}</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <p className="text-sm text-[#4A4A4A]" data-testid="journeys-count">{journeys ? `${journeys.length} journeys` : "Loading…"}</p>
          <div className="flex gap-3"><CurrencyToggle /><CartLink /></div>
        </div>
        <SectionReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="journeys-grid">
            {(journeys || []).map((j) => <JourneyCard key={j.slug} j={j} />)}
          </div>
        </SectionReveal>
      </section>
      <Footer />
    </div>
  );
}
